import { useEffectAfterFirstRender } from '@app/hooks/use-effect-after-first-render';
import { FavoritedMenuItemsStorage } from '@app/services/storage';
import { Complement, ComplementGroup, Menu, MenuItem } from '@app/types';
import { calculateTotalItemPrice } from '@app/utils/calculus';
import { isGroupValidOnBasisOfComplementsSelected } from '@app/utils/menu';
import { deepCloneObject } from '@app/utils/object';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ModalActionType } from '.';
import { MenuItemDetailsSession, MenuItemSessionData } from './storage';

export function useSaveInSession(menuItemId: string) {
  const data = useRef<MenuItemSessionData>({ amount: 0, complements: {} });

  useEffect(() => {
    const previousData = MenuItemDetailsSession.getMenuItemDataPreviouslyFilled(menuItemId);
    if (previousData) data.current = previousData;
  }, []);

  const updateComplement = useCallback(
    (complement: Complement) => {
      const complementId = complement.id;
      const groupId = complement.grupoId;
      const amount = complement.amount ?? 0;

      if (!data.current.complements[groupId]) data.current.complements[groupId] = {};
      data.current.complements[groupId][complementId] = amount;
      MenuItemDetailsSession.setMenuItemFilled(menuItemId, data.current);
    },
    [menuItemId]
  );

  const removeGroup = useCallback(
    (groupId: string) => {
      delete data.current.complements[groupId];
      MenuItemDetailsSession.setMenuItemFilled(menuItemId, data.current);
    },
    [menuItemId]
  );

  const clear = useCallback(() => {
    MenuItemDetailsSession.removeMenuItemPreviousFilled(menuItemId);
  }, [menuItemId]);

  const updateAmount = useCallback(
    (amount: number) => {
      data.current.amount = amount;
      MenuItemDetailsSession.setMenuItemFilled(menuItemId, data.current);
    },
    [menuItemId]
  );

  return { data, updateComplement, updateAmount, removeGroup, clear };
}
export type ValidationArray = (boolean | undefined)[];

type ValidationsHookReturnType = [
  ValidationArray,
  Dispatch<SetStateAction<ValidationArray>>,
  boolean[],
  (validationsParam?: ValidationArray) => number,
  () => boolean,
  () => void
];

export function useComplementsGroupsValidation(groups: ComplementGroup[] = []): ValidationsHookReturnType {
  const [validations, setValidations] = useState<ValidationArray>(() =>
    groups.map((group) => isGroupValidOnBasisOfComplementsSelected(group))
  );

  const revalidateAllGroups = useCallback(() => {
    const newValidations = [...validations];
    groups.forEach((group, index) => {
      newValidations[index] = isGroupValidOnBasisOfComplementsSelected(group);
    });
    setValidations(newValidations);
  }, []);

  const getInvalidComplementGroupIndex = useCallback(
    (validationsParam?: ValidationArray) => {
      const finalValidations = validationsParam ?? validations;
      return finalValidations.findIndex((value, index) => !value && !groups[index].opcional);
    },
    [groups, validations]
  );

  const existsSomeComplementGroupInvalid = useCallback(() => {
    return validations.some((value, index) => !value && !groups[index].opcional);
  }, [groups, validations]);

  const shouldHighlightGroupStats = useMemo(() => {
    const invalidComplementGroupIndex = getInvalidComplementGroupIndex();
    const allRequiredComplementGroupsHasFilled = invalidComplementGroupIndex === -1;

    const nextComplementGroupValid = validations.findIndex(
      (value, indexGroup) => value === true && indexGroup > invalidComplementGroupIndex
    );

    return groups.map((group, index) => {
      if (allRequiredComplementGroupsHasFilled) return false;
      return !group.opcional && !validations[index] && index < nextComplementGroupValid;
    });
  }, [getInvalidComplementGroupIndex, groups]);

  return [
    validations,
    setValidations,
    shouldHighlightGroupStats,
    getInvalidComplementGroupIndex,
    existsSomeComplementGroupInvalid,
    revalidateAllGroups,
  ];
}

export function useComplementGroupsScroll() {
  const [nextComplementGroupToScroll, setNextComplementGroupToScroll] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!nextComplementGroupToScroll) return;

    if (nextComplementGroupToScroll.scrollIntoView) {
      nextComplementGroupToScroll.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      console.warn(`HTMLElement ${nextComplementGroupToScroll} is unable scroll to it`);
    }
    setNextComplementGroupToScroll(null);
  }, [nextComplementGroupToScroll]);

  return setNextComplementGroupToScroll;
}

export function getInitialTotalItem(
  item: MenuItem,
  modalActionType: ModalActionType,
  menuItemHasSessionDataInjected: boolean
) {
  if (modalActionType === ModalActionType.Editing || menuItemHasSessionDataInjected)
    return calculateTotalItemPrice(item);
  return item.preco;
}

export function createInvalidMessage(menuItem: MenuItem, invalidComplementGroup: ComplementGroup) {
  return `Antes de adicionar ${menuItem.nome} na sacola, você precisa selecionar pelo menos ${invalidComplementGroup.minimo} ${invalidComplementGroup.nome}`;
}

export function useFavoritedState(menuItemId: string) {
  const [isFavorited, setIsFavorited] = useState(false);

  useEffect(() => {
    setIsFavorited(FavoritedMenuItemsStorage.isFavorited(menuItemId));
  }, []);

  useEffectAfterFirstRender(() => {
    if (isFavorited) FavoritedMenuItemsStorage.add(menuItemId);
    else FavoritedMenuItemsStorage.remove(menuItemId);
  }, [isFavorited]);

  const toggleIsFavorited = useCallback(() => {
    setIsFavorited((draft) => !draft);
  }, []);

  return { isFavorited, toggleIsFavorited };
}
